import React, { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react'
import { UsersTypes } from '../../Data';

import loadLightIcon from '../../images/icons/load-light.gif'
import loadDarkIcon from '../../images/icons/load-dark.gif'
import { useTheme } from '../../context/ThemeContext';
import { DownIcon, UpIcon } from '../iconSet/IconSet';

type UserDataRowType = {
    id: string;
    name: string;
    department: string;
}

type UserDataTablePropsType = {
    data: UsersTypes[]
    setSelectedRows: Dispatch<React.SetStateAction<UsersTypes[]>>
    isLoading: boolean;
}

type SortTypes = "DES" | "ASE"

// Null-safe string comparator. Treats null/undefined as an empty string so
// .localeCompare never gets called on a null value.
const compareStrings = (a: string | null | undefined, b: string | null | undefined): number => {
    const valA = a ?? '';
    const valB = b ?? '';
    return valA.localeCompare(valB);
}

const UserDataTable = (props: UserDataTablePropsType) => {

    const { theme } = useTheme()

    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [filteredData, setFilteredData] = useState<UsersTypes[]>([])
    const [slicedData, setSlicedData] = useState<UsersTypes[][]>([])

    // true = A -> Z
    // false = Z -> A
    const [usernameSort, setUsernameSort] = useState<Boolean>(true)
    const usernameSortHandle = () => {
        setUsernameSort(prevState => !prevState)
    }
    const [departmentSort, setDepartmentSort] = useState<boolean>(true)
    const departmentSortHandle = () => {
        setDepartmentSort(prevState => !prevState)
    }

    // Rows missing firstname/department are dropped here, before they ever
    // reach filteredData/slicedData. Previously this filtering happened at
    // render time, AFTER pagination was computed from the unfiltered data —
    // which threw off page counts, page contents, and "select all".
    const hasRequiredFields = (row: UsersTypes) => !!row.firstname && !!row.department;

    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                return usernameSort
                    ? compareStrings(a.firstname, b.firstname)
                    : compareStrings(b.firstname, a.firstname)
            })
        })
    }, [usernameSort])

    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                return departmentSort
                    ? compareStrings(a.department, b.department)
                    : compareStrings(b.department, a.department)
            })
        })
    }, [departmentSort])

    const paginationList = (data: UsersTypes[], size: number): UsersTypes[][] => {
        const result: UsersTypes[][] = []

        if (size >= data.length) {
            return [data]
        }

        let start = 0

        while (start < data.length) {
            result.push(data.slice(start, start + size))
            start += size
        }

        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
                console.log(">>>> good next")
            }

        } else if (side === "prev") {
            if (0 <= selectedPage - 1) {
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    // Handle slice count changed
    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    // handle search text change
    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim().toLowerCase();
        if (event.target.value === '') {
            setFilteredData(props.data.filter(hasRequiredFields))
        } else {
            setFilteredData(prevState => props.data
                .filter(hasRequiredFields)
                .filter(item =>
                    (item.username?.toLowerCase()?.includes(value) ?? false) ||
                    (item.department?.toLowerCase()?.includes(value) ?? false)))
        }
    }

    useEffect(() => {
        setFilteredData(prevState => {
            return props.data
                .filter(hasRequiredFields)
                .sort((a, b) => {
                    return usernameSort
                        ? compareStrings(a.firstname, b.firstname)
                        : compareStrings(b.firstname, a.firstname)
                })
        })
    }, [props.data])

    useEffect(() => {
        if (rowNumber.current) {
            setSlicedData(prevState => paginationList(filteredData, sliceCount))
        }
    }, [sliceCount, filteredData])

    const handleCheckBoxSelected = (event: ChangeEvent<HTMLInputElement>, row: UsersTypes) => {

        setSelectedCodes(prev => {
            const updated = new Set(prev);
            if (event.target.checked) {
                updated.add(event.target.name);
            } else {
                updated.delete(event.target.name);
            }
            return updated;
        })

        if (event.target.checked) {
            props.setSelectedRows(prevState => [...prevState, row])
        } else {
            props.setSelectedRows(prevState => prevState.filter(item => item.username !== row.username))
        }
    }

    const handleSelectAllCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        const allData = filteredData ?? [];
        if (isChecked) {
            const allCodes = new Set(allData.map(row => row.username ?? ''));
            setSelectedCodes(allCodes);

            props.setSelectedRows(allData);
        }
        else {
            setSelectedCodes(new Set());
            props.setSelectedRows([]);
        }
    }

    return (
        <div className='subTable-wrapper'>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} defaultValue={10} min={10} onChange={(e) => handleSliceCountChange(e)} /> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here' /></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{ gridTemplateColumns: `3rem 1fr 1fr`, width: '100%' }}>
                        <span>
                            <input type="checkbox" className='select-all-checkbox' onChange={(e) => handleSelectAllCheckbox(e)} />
                        </span>
                        <span onClick={(e) => usernameSortHandle()}>
                            First Name
                            <div>
                                {usernameSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}

                            </div>

                        </span>
                        <span onClick={(e) => departmentSortHandle()}>
                            Department
                            <div>
                                {departmentSort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}

                            </div>
                        </span>
                    </div>
                </div>
                {props.isLoading ? (
                    <div className='loading-wrapper'>
                        <img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} alt="" />
                        <span>Searching user data...</span>
                    </div>
                ) : (
                    slicedData[selectedPage] && slicedData[selectedPage]
                        .map((row, index) => (
                            <div key={index} className='table-wrapper'>
                                <div className='table-row' style={{ gridTemplateColumns: `3rem 1fr 1fr`, width: '100%' }}>
                                    <span style={{ justifyContent: 'center' }}>
                                        <input type="checkbox" name={`${row.username}`} onChange={(e) => handleCheckBoxSelected(e, row)} checked={selectedCodes.has(`${row.username}`)} />
                                    </span>
                                    <span>{row.firstname}</span>
                                    <span>{row.department}</span>
                                </div>
                            </div>
                        ))
                )}
            </div>

            {!props.isLoading && filteredData.length === 0 && (
                <div className='table-no-data-wrapper'>
                    <span>no any data available</span>
                </div>
            )}
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default UserDataTable